/**
 * AI Service using Supabase Edge Functions
 * Provides AI-powered slide generation via Supabase Edge Functions with OpenRouter
 */

import { supabase } from './supabase.js'

class AIService {
  constructor() {
    console.log('🚀 AI Service initialized - using Supabase Edge Functions')
    console.log('📝 Model: meta-llama/llama-3.3-70b-instruct (paid) via Supabase')
    console.log('🔒 API key securely stored in Supabase secrets')
  }

  /**
   * Generate slide content using AI via Supabase Edge Function
   * @param {string} prompt - The user's prompt for slide generation
   * @param {string} title - The project title
   * @param {string} type - Type of generation ('prompt' or 'script')
   * @param {string} scriptContent - Script content if type is 'script'
   * @returns {Promise<Object>} - Generated slide data with extended structure
   */
  async generateSlideContent(prompt, title, type = 'prompt', scriptContent = null, advancedData = {}) {
    try {
      console.log('📤 Calling Supabase Edge Function for slide generation...')
      console.log('⏱️ This may take 30-60 seconds for AI processing...')
      
      const { data, error } = await supabase.functions.invoke('generate-slides', {
        body: {
          prompt,
          title,
          type,
          scriptContent,
          advancedData
        }
      })

      if (error) {
        console.error('Supabase Edge Function error:', error)
        console.warn('Edge Function not deployed or CORS issue - using enhanced mock data')
        return this.getEnhancedMockContent(prompt, title, type)
      }

      if (data.fallback) {
        console.warn('🚨 AI service returned fallback response - this means the Edge Function failed')
        
        if (data.isRateLimit) {
          console.error('💸 RATE LIMIT: Free model quota exceeded - need to wait or use paid model')
        } else if (data.isPaymentRequired) {
          console.error('💳 PAYMENT REQUIRED: Free tier exhausted - upgrading to paid model')
        } else {
          console.warn('This could be due to: API key issues, server errors, or Edge Function problems')
        }
        
        console.warn('Using enhanced mock data as fallback')
        return this.getEnhancedMockContent(prompt, title, type)
      }

      if (data.success && data.analysis) {
        console.log('✅ Successfully received AI-generated content')
        console.log('🔍 Analysis object keys:', Object.keys(data.analysis))
        
        const analysis = data.analysis
        
        // Log key fields to verify they're clean
        console.log('📊 Received content preview:', {
          title: analysis.title,
          tagline: analysis.tagline,
          synopsis: analysis.synopsis ? analysis.synopsis.substring(0, 100) + '...' : 'None',
          synopsisType: typeof analysis.synopsis,
          genres: analysis.genres,
          format: analysis.format,
          formatDetails: analysis.formatDetails,
          productionApproach: analysis.productionApproach,
          talentStrategy: analysis.talentStrategy
        })
        
        // Specific logging for the arrays that are missing content
        console.log('🔍 Array content debug:', {
          formatDetailsExists: !!analysis.formatDetails,
          formatDetailsLength: analysis.formatDetails?.length || 0,
          formatDetailsContent: analysis.formatDetails,
          productionApproachExists: !!analysis.productionApproach,
          productionApproachLength: analysis.productionApproach?.length || 0,
          productionApproachContent: analysis.productionApproach,
          talentStrategyExists: !!analysis.talentStrategy,
          talentStrategyLength: analysis.talentStrategy?.length || 0,
          talentStrategyContent: analysis.talentStrategy
        })
        
        // The Edge Function should have already validated and cleaned everything
        // Just verify we have the minimum required fields
        if (!analysis.title || !analysis.synopsis) {
          console.error('❌ Missing required fields from AI service')
          throw new Error('Invalid analysis object received from AI service')
        }
        
        console.log('✅ Analysis validation passed')
        return analysis
      }

      throw new Error('Invalid response from AI service')
      
    } catch (error) {
      console.error('Error calling AI service:', error)
      console.warn('Falling back to enhanced mock content generation')
      return this.getEnhancedMockContent(prompt, title, type)
    }
  }

  /**
   * Generate AI assistant response for slide improvement
   * @param {string} userMessage - User's message/question
   * @param {Object} slideContext - Current slide data for context
   * @returns {Promise<Object>} - AI response with content and suggested actions
   */
  async generateAssistantResponse(userMessage, slideContext = null) {
    try {
      // For now, we'll use a simple approach for assistant responses
      // This could be expanded to use a separate edge function if needed
      const response = this.getContextualAssistantResponse(userMessage, slideContext)
      
      return {
        content: response.content,
        actions: response.actions
      }
    } catch (error) {
      console.error('Error generating assistant response:', error)
      return this.getFallbackAssistantResponse(userMessage)
    }
  }

  /**
   * Improve existing slide content using AI
   * @param {Object} slide - Current slide data
   * @param {string} improvementType - Type of improvement requested
   * @returns {Promise<Object>} - Improved slide data
   */
  async improveSlideContent(slide, improvementType = 'general') {
    try {
      // For slide improvements, we'll apply programmatic improvements
      // This maintains the template structure while enhancing content
      return this.applySlideImprovements(slide, improvementType)
    } catch (error) {
      console.error('Error improving slide content:', error)
      return slide
    }
  }

  // Helper methods for contextual responses and improvements

  getContextualAssistantResponse(userMessage, slideContext) {
    const messageLower = userMessage.toLowerCase()
    
    // Analyze message intent
    if (messageLower.includes('color') || messageLower.includes('colour')) {
      return {
        content: "I can help you improve your slide colors! Here are some suggestions:\n\n• **Contrast**: Ensure text has good contrast against backgrounds (WCAG guidelines)\n• **Brand Colors**: Use consistent colors that match your project's tone\n• **Hierarchy**: Use different colors to show information importance\n• **Accessibility**: Avoid red-green combinations for colorblind accessibility\n\nFor your current slide, I recommend using a professional color palette with high contrast.",
        actions: ['Apply Professional Colors', 'Improve Contrast', 'Match Project Tone']
      }
    }
    
    if (messageLower.includes('font') || messageLower.includes('typography') || messageLower.includes('text')) {
      return {
        content: "Let me help you enhance your typography!\n\n• **Hierarchy**: Use different font sizes to guide the reader's eye\n• **Readability**: Ensure fonts are large enough and easy to read\n• **Consistency**: Stick to 2-3 font weights maximum\n• **Professional**: Use clean, modern fonts for presentations\n\nI can optimize your current slide's typography for better impact.",
        actions: ['Improve Typography', 'Fix Font Sizes', 'Enhance Readability']
      }
    }
    
    if (messageLower.includes('layout') || messageLower.includes('position') || messageLower.includes('spacing')) {
      return {
        content: "Great question about layout! Here's how we can improve it:\n\n• **White Space**: Add breathing room around elements\n• **Alignment**: Ensure elements are properly aligned\n• **Balance**: Distribute visual weight evenly\n• **Flow**: Guide the viewer's eye through the content logically\n\nI can reorganize your slide for better visual hierarchy.",
        actions: ['Optimize Layout', 'Improve Spacing', 'Balance Elements']
      }
    }
    
    if (messageLower.includes('content') || messageLower.includes('text') || messageLower.includes('writing')) {
      return {
        content: "I can help make your content more engaging!\n\n• **Clarity**: Make key points clear and concise\n• **Structure**: Organize information in a logical flow\n• **Impact**: Use powerful language that resonates\n• **Brevity**: Keep slides focused on core messages\n\nLet me help restructure your content for maximum impact.",
        actions: ['Enhance Content', 'Improve Structure', 'Add Impact']
      }
    }

    // General response
    return {
      content: "I'm here to help improve your slides! I can assist with:\n\n• **Visual Design**: Colors, typography, and layout optimization\n• **Content Structure**: Making your message clear and impactful\n• **Professional Polish**: Ensuring your slides look industry-standard\n• **Audience Engagement**: Making content more compelling\n\nWhat specific aspect would you like to focus on?",
      actions: ['Improve Overall Design', 'Enhance Content', 'Professional Polish']
    }
  }

  applySlideImprovements(slide, improvementType) {
    if (!slide || !slide.elements) return slide

    const improvedSlide = { ...slide }
    improvedSlide.elements = slide.elements.map(element => {
      const improvedElement = { ...element }

      switch (improvementType) {
        case 'typography':
          if (element.type === 'text') {
            const currentSize = parseInt(element.style?.fontSize) || 16
            improvedElement.style = {
              ...element.style,
              fontSize: currentSize > 30 ? `${Math.min(48, currentSize * 1.2)}px` : `${Math.max(14, currentSize * 1.1)}px`,
              fontWeight: currentSize > 30 ? 'bold' : element.style?.fontWeight || 'normal',
              lineHeight: currentSize > 20 ? '1.2' : '1.4'
            }
          }
          break

        case 'color':
          if (element.type === 'text') {
            const fontSize = parseInt(element.style?.fontSize) || 16
            improvedElement.style = {
              ...element.style,
              color: fontSize > 30 ? '#1f2937' : '#374151'
            }
          }
          break

        case 'content':
          if (element.type === 'text' && element.content && element.content.length > 150) {
            // Break long content into more digestible chunks
            const sentences = element.content.split('. ')
            if (sentences.length > 1) {
              improvedElement.content = sentences.slice(0, 2).join('. ') + '.'
            }
          }
          break

        case 'layout':
          // Improve spacing and positioning
          if (element.style?.position) {
            improvedElement.style = {
              ...element.style,
              position: {
                ...element.style.position,
                x: Math.max(50, element.style.position.x),
                y: Math.max(30, element.style.position.y)
              }
            }
          }
          break
      }

      return improvedElement
    })

    return improvedSlide
  }

  getEnhancedMockContent(prompt, title, type = 'prompt') {
    // Analyze the prompt to create more relevant content
    const promptLower = prompt.toLowerCase()
    
    // Extract themes and tone from prompt
    let themes = ['Growth', 'Challenge', 'Resolution']
    let tone = 'Engaging and thoughtful'
    let genres = ['Drama']
    let format = 'Feature Film'
    
    // Genre detection
    if (promptLower.includes('horror') || promptLower.includes('scary')) {
      genres = ['Horror', 'Thriller']
      tone = 'Dark and suspenseful'
      themes = ['Fear', 'Survival', 'Mystery']
    } else if (promptLower.includes('comedy') || promptLower.includes('funny')) {
      genres = ['Comedy']
      tone = 'Light and entertaining'
      themes = ['Humor', 'Relationships', 'Growth']
    } else if (promptLower.includes('romance') || promptLower.includes('love')) {
      genres = ['Romance', 'Drama']
      tone = 'Heartwarming and emotional'
      themes = ['Love', 'Connection', 'Destiny']
    } else if (promptLower.includes('action') || promptLower.includes('adventure')) {
      genres = ['Action', 'Adventure']
      tone = 'Thrilling and dynamic'
      themes = ['Heroism', 'Justice', 'Victory']
    } else if (promptLower.includes('sci-fi') || promptLower.includes('space') || promptLower.includes('future')) {
      genres = ['Sci-Fi', 'Drama']
      tone = 'Thought-provoking and innovative'
      themes = ['Technology', 'Humanity', 'Future']
    }
    
    // Format detection
    if (promptLower.includes('series') || promptLower.includes('episodes')) {
      format = 'Limited Series'
    } else if (promptLower.includes('short') || promptLower.includes('micro')) {
      format = 'Short Film'
    }
    
    // Create contextual tagline
    const taglines = {
      'Horror': 'Fear has a new face',
      'Comedy': 'Laughter is the best medicine',
      'Romance': 'Love conquers all',
      'Action': 'Justice never rests',
      'Sci-Fi': 'The future is now',
      'Drama': 'Every story has a truth'
    }
    
    const tagline = taglines[genres[0]] || 'A compelling story awaits'
    
    // Generate contextual synopsis
    let synopsis = `${title} tells the story of compelling characters navigating the complexities of ${themes.join(', ').toLowerCase()}. `
    
    if (type === 'script') {
      synopsis += 'Based on the provided script, this adaptation brings rich character development and engaging narrative to life. '
    }
    
    synopsis += `With its ${tone.toLowerCase()} approach, this ${format.toLowerCase()} explores universal themes while delivering entertainment that resonates with modern audiences. The story weaves together elements of ${genres.join(' and ').toLowerCase()} to create a unique viewing experience that both entertains and inspires.`
    
    return {
      title: title || "Enhanced AI-Generated Project",
      tagline,
      synopsis,
      genres,
      targetAudience: this.getTargetAudience(genres),
      tone,
      format,
      themes,
      characters: this.generateContextualCharacters(genres, themes),
      supportingCast: this.generateSupportingCast(genres),
      formatDetails: this.getFormatDetails(format),
      productionApproach: this.getProductionApproach(genres),
      talentStrategy: this.getTalentStrategy(genres, format)
    }
  }

  generateContextualCharacters(genres, themes) {
    const characters = []
    
    // Protagonist based on genre
    if (genres.includes('Horror')) {
      characters.push({
        name: "Sarah Chen",
        role: "Protagonist",
        age: 28,
        traits: ["Resourceful", "Determined", "Intuitive"],
        appeal: "Relatable survivor who faces terror with intelligence"
      })
    } else if (genres.includes('Comedy')) {
      characters.push({
        name: "Alex Murphy",
        role: "Protagonist", 
        age: 32,
        traits: ["Witty", "Charming", "Optimistic"],
        appeal: "Loveable character with perfect comedic timing"
      })
    } else if (genres.includes('Romance')) {
      characters.push({
        name: "Emma Rodriguez",
        role: "Protagonist",
        age: 29,
        traits: ["Passionate", "Independent", "Vulnerable"],
        appeal: "Strong romantic lead with authentic emotional depth"
      })
    } else {
      characters.push({
        name: "Jordan Rivers",
        role: "Protagonist",
        age: 30,
        traits: ["Complex", "Driven", "Authentic"],
        appeal: "Multi-dimensional character with compelling arc"
      })
    }
    
    // Antagonist based on genre
    if (genres.includes('Horror')) {
      characters.push({
        name: "Dr. Vincent Kane",
        role: "Antagonist",
        age: 45,
        traits: ["Calculating", "Mysterious", "Intelligent"],
        appeal: "Terrifying villain with psychological depth"
      })
    } else if (genres.includes('Comedy')) {
      characters.push({
        name: "Margaret Thornfield",
        role: "Rival",
        age: 38,
        traits: ["Uptight", "Competitive", "Secretly insecure"],
        appeal: "Perfect comedic foil with hidden vulnerability"
      })
    } else {
      characters.push({
        name: "Marcus Sterling",
        role: "Antagonist", 
        age: 42,
        traits: ["Charismatic", "Ruthless", "Complex"],
        appeal: "Compelling villain with understandable motives"
      })
    }
    
    return characters
  }

  generateSupportingCast(genres) {
    if (genres.includes('Horror')) {
      return [
        { name: "Detective Riley Martinez", role: "Investigator", description: "Seasoned detective who becomes an unlikely ally in the supernatural investigation" },
        { name: "Professor Elena Vasquez", role: "Expert", description: "Paranormal researcher with crucial knowledge about the dark forces at play" }
      ]
    } else if (genres.includes('Comedy')) {
      return [
        { name: "Charlie Thompson", role: "Best Friend", description: "Loyal sidekick who provides both support and comic relief in crucial moments" },
        { name: "Mrs. Patterson", role: "Mentor", description: "Wise elderly neighbor with unexpected insights and perfect timing" }
      ]
    } else {
      return [
        { name: "Sam Williams", role: "Mentor", description: "Experienced guide who helps the protagonist navigate critical challenges" },
        { name: "Casey Morgan", role: "Ally", description: "Trusted companion who brings unique skills and unwavering loyalty" }
      ]
    }
  }

  getTargetAudience(genres) {
    if (genres.includes('Horror')) return 'Adults 18-35, horror enthusiasts'
    if (genres.includes('Comedy')) return 'General audience, comedy fans 16-50'
    if (genres.includes('Romance')) return 'Adults 18-45, romance viewers'
    if (genres.includes('Sci-Fi')) return 'Adults 16-45, sci-fi enthusiasts'
    return 'General audience 18-45'
  }

  getFormatDetails(format) {
    if (format === 'Limited Series') {
      return [
        '8-episode limited series',
        '45-60 minute episodes',
        'Streaming platform distribution',
        'Binge-worthy format',
        'Complete story arc',
        'High production value'
      ]
    } else if (format === 'Short Film') {
      return [
        '15-30 minute runtime',
        'Festival circuit distribution',
        'Proof of concept potential',
        'Focused narrative',
        'Independent production',
        'Award season eligible'
      ]
    } else {
      return [
        '90-120 minute feature',
        'Theatrical release',
        'International distribution',
        'Awards consideration',
        'Franchise potential',
        'Cross-platform marketing'
      ]
    }
  }

  getProductionApproach(genres) {
    if (genres.includes('Horror')) {
      return [
        'Atmospheric cinematography with practical effects',
        'Cost-effective location shooting',
        'Strategic use of CGI for maximum impact',
        'Sound design emphasis for psychological tension',
        'Controlled production schedule',
        'Genre-experienced crew'
      ]
    } else if (genres.includes('Comedy')) {
      return [
        'Improvisation-friendly shooting style',
        'Multiple take approach for best comedic timing',
        'Collaborative creative environment',
        'Efficient production schedule',
        'Comedy-specialized crew',
        'Audience test screening integration'
      ]
    } else {
      return [
        'Professional industry-standard production',
        'Efficient workflow and scheduling',
        'High-quality cinematography and sound',
        'Experienced department heads',
        'Strategic location and studio use',
        'Post-production excellence'
      ]
    }
  }

  getTalentStrategy(genres, format) {
    if (genres.includes('Horror')) {
      return [
        'Genre-experienced actors with strong emotional range',
        'Emerging talent mixed with horror veterans',
        'Strong social media presence for genre marketing',
        'Horror convention and festival circuit promotion'
      ]
    } else if (genres.includes('Comedy')) {
      return [
        'Comedy-proven talent with improvisational skills',
        'Mix of established comedic actors and fresh faces',
        'Strong social media and stand-up comedy connections',
        'Late-night and comedy show promotion potential'
      ]
    } else {
      return [
        'Versatile actors with strong dramatic range',
        'Strategic mix of established and emerging talent',
        'Awards season positioning potential',
        'International appeal and marketability'
      ]
    }
  }

  getFallbackSlideContent(prompt, title) {
    // Keep original fallback for backward compatibility
    return {
      title: title || "AI-Generated Project",
      tagline: "Innovation meets storytelling",
      synopsis: "A compelling narrative that explores the depths of human experience through innovative storytelling techniques. This project combines traditional narrative elements with modern production approaches to create engaging content that resonates with contemporary audiences.",
      genres: ["Drama", "Thriller"],
      targetAudience: "Adults 18-45, sophisticated viewers",
      tone: "Thoughtful and engaging",
      format: "Limited Series",
      themes: ["Innovation", "Human Connection", "Transformation"],
      characters: [
        {
          name: "Alex Rivera",
          role: "Protagonist",
          age: 32,
          traits: ["Innovative", "Determined", "Complex"],
          appeal: "Relatable professional facing modern challenges"
        },
        {
          name: "Morgan Blake",
          role: "Antagonist",
          age: 45,
          traits: ["Charismatic", "Manipulative", "Intelligent"],
          appeal: "Complex villain with understandable motives"
        }
      ],
      supportingCast: [
        {
          name: "Jordan Chen",
          role: "Mentor Figure",
          description: "Wise advisor who guides the protagonist through critical decisions"
        },
        {
          name: "Taylor Wilson",
          role: "Ally",
          description: "Loyal friend who provides both support and comic relief"
        }
      ],
      formatDetails: [
        "8-episode limited series",
        "45-60 minute episodes",
        "Streaming platform distribution",
        "High production value"
      ],
      productionApproach: [
        "Innovative production techniques",
        "Cost-effective digital workflows",
        "Sustainable production practices",
        "Collaborative creative process"
      ],
      talentStrategy: [
        "Emerging talent mixed with established names",
        "Diverse casting for authentic representation",
        "Strong social media presence for marketing",
        "Awards season positioning potential"
      ]
    }
  }

  getFallbackAssistantResponse(userMessage) {
    return {
      content: "I'd be happy to help improve your slides! I can assist with visual design, content structure, typography, color schemes, and overall presentation effectiveness. What specific aspect would you like to focus on?",
      actions: ["Improve Typography", "Enhance Colors", "Restructure Content", "Optimize Layout"]
    }
  }

  /**
   * Generate project description and tags using AI
   * @param {string} prompt - The user's prompt for the project
   * @param {string} title - The project title
   * @param {string} type - Type of generation ('prompt' or 'script')
   * @returns {Promise<Object>} - Generated description and tags
   */
  async generateProjectMetadata(prompt, title, type = 'prompt') {
    try {
      console.log('📤 Generating project description and tags...')
      
      const { data, error } = await supabase.functions.invoke('generate-project-metadata', {
        body: {
          prompt,
          title,
          type
        }
      })

      if (error) {
        console.warn('Edge Function not available - using fallback metadata generation')
        return this.generateMockMetadata(prompt, title, type)
      }

      return data
    } catch (error) {
      console.error('❌ Error generating project metadata:', error)
      return this.generateMockMetadata(prompt, title, type)
    }
  }

  /**
   * Generate mock metadata when AI service is unavailable
   * @param {string} prompt - The user's prompt
   * @param {string} title - The project title
   * @param {string} type - Type of generation
   * @returns {Object} - Mock description and tags
   */
  generateMockMetadata(prompt, title, type) {
    // Extract genre/theme from prompt for smart tag generation
    const lowerPrompt = prompt.toLowerCase()
    const genres = ['drama', 'action', 'comedy', 'thriller', 'horror', 'sci-fi', 'romance', 'fantasy', 'documentary']
    const detectedGenre = genres.find(genre => lowerPrompt.includes(genre)) || 'drama'
    
    // Generate a concise description (50-150 words)
    const description = type === 'script' 
      ? `${title} is a compelling ${detectedGenre} production developed from an original screenplay. The project explores themes and characters that resonate with contemporary audiences while delivering engaging storytelling and professional production values.`
      : `${title} is an exciting ${detectedGenre} project that brings together innovative storytelling with compelling characters. ${prompt.substring(0, 100)}... This production aims to captivate audiences through its unique approach to ${detectedGenre} entertainment.`
    
    // Generate relevant tags
    const baseTags = [detectedGenre, 'professional', 'original']
    const thematicTags = []
    
    // Add thematic tags based on content
    if (lowerPrompt.includes('character') || lowerPrompt.includes('story')) thematicTags.push('character-driven')
    if (lowerPrompt.includes('action') || lowerPrompt.includes('fight')) thematicTags.push('action-packed')
    if (lowerPrompt.includes('mystery') || lowerPrompt.includes('suspense')) thematicTags.push('suspenseful')
    if (lowerPrompt.includes('emotion') || lowerPrompt.includes('heart')) thematicTags.push('emotional')
    if (lowerPrompt.includes('future') || lowerPrompt.includes('technology')) thematicTags.push('futuristic')
    
    const tags = [...baseTags, ...thematicTags.slice(0, 2)].slice(0, 5)
    
    return {
      description,
      tags,
      genre: detectedGenre
    }
  }
}

// Export singleton instance
const aiService = new AIService()
export default aiService
